import React, { useEffect, useMemo, useState } from "react";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Button, Description, Flexbox, Label, Modal, ModalBody, ModalHeader, Stack, shootToast } from "codekit";
import Tarjekit from "../Tablerox/Tarjewid";
import Delcfirm from "../Confirmx/Delcfirm";

import {
  useAgregarMipuestoWidgetMutation,
  useLazyGetCatalogoDashboardsQuery,
  useLazyGetMipuestoWidgetsQuery,
  useOrdenarMipuestoWidgetsMutation,
  useQuitarMipuestoWidgetMutation,
  useRestablecerMipuestoWidgetsMutation,
} from "../../libs/redux/slices/Mipuesto/mipuestoApiSlice";

/**
 * Una tarjeta ADICIONAL del escritorio: la tarjeta única del kit (Tarjewid)
 * envuelta en el sortable de dnd-kit para reordenar arrastrando la cabecera.
 */
const Tarjewid = ({ widget, onQuitar }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(widget.GRAIDXXX),
  });
  return (
    <Tarjekit
      widget={widget}
      onQuitar={onQuitar}
      arrastre={{ ...attributes, ...listeners }}
      refTarjeta={setNodeRef}
      estilo={{ transform: CSS.Transform.toString(transform), transition }}
    />
  );
};

/** Una tarjeta FIJA de la plantilla del cargo: sin arrastre ni ✕ */
const Tarjefij = ({ widget }) => <Tarjekit widget={widget} />;

/**
 * Mi escritorio: el lienzo de widgets de Mi Puesto (FASE 5, pieza 2).
 *
 * Vive debajo de las franjas de Mi día. Regla (Diego 2026-09-16): la
 * PLANTILLA del cargo va siempre y de primero, fija (sin ✕ ni arrastre);
 * personalizar es AGREGAR tableros adicionales después, solo con el permiso
 * "Personalizar escritorio" (PUEDEPER); esos sí se reordenan y se quitan.
 * Los widgets salen del catálogo gateado por el menú del usuario; lo
 * adicional persiste en CIMS0112. El grid es CSS por tamaño (S/M/L) y el
 * arrastre usa dnd-kit, que sí soporta grillas.
 *
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-08-11
 */
const Miescrix = () => {
  const [widgets, setWidgets] = useState(null);
  //ESPERSXX = tiene tableros adicionales; PUEDEPER = permiso de personalizar
  const [esPersonal, setEsPersonal] = useState(false);
  const [puedePersonalizar, setPuedePersonalizar] = useState(false);
  const [cargoDes, setCargoDes] = useState("");
  const [showGaleria, setShowGaleria] = useState(false);
  //Quitar los adicionales pide confirmación: el botón se puede oprimir sin culpa (Diego 2026-09-16)
  const [showConfirmQuitar, setShowConfirmQuitar] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [getWidgets] = useLazyGetMipuestoWidgetsQuery();
  const [getCatalogo] = useLazyGetCatalogoDashboardsQuery();
  const [agregarWidget] = useAgregarMipuestoWidgetMutation();
  const [quitarWidget] = useQuitarMipuestoWidgetMutation();
  const [ordenarWidgets] = useOrdenarMipuestoWidgetsMutation();
  const [restablecerWidgets, { isLoading: isRestableciendo }] = useRestablecerMipuestoWidgetsMutation();

  //La respuesta trae {ESPERSXX, PUEDEPER, CARGODES, widgets[{..., FIJOXXXX}]}
  const aplicarEscritorio = (data) => {
    setWidgets(data?.widgets ?? []);
    setEsPersonal(Boolean(data?.ESPERSXX ?? false));
    setPuedePersonalizar(Boolean(data?.PUEDEPER ?? false));
    setCargoDes(String(data?.CARGODES ?? ""));
  };

  useEffect(() => {
    getWidgets()
      .unwrap()
      .then((response) => aplicarEscritorio(response?.data))
      .catch(() => setWidgets([]));
  }, [getWidgets]);

  const handleRestablecer = async () => {
    await restablecerWidgets()
      .unwrap()
      .then((response) => {
        aplicarEscritorio(response?.data);
        shootToast({ variant: "success", text: `Tableros adicionales retirados: queda la plantilla de ${cargoDes || "tu cargo"}`, duration: 3000, autoHide: true });
      })
      .catch(() => shootToast({ variant: "error", text: "No se pudieron retirar los tableros adicionales", duration: 3000, autoHide: true }));
  };

  const handleAbrirGaleria = async () => {
    setShowGaleria(true);
    await getCatalogo()
      .unwrap()
      .then((response) => setCatalogo(response?.data ?? []))
      .catch(() => setCatalogo([]));
  };

  const handleAgregar = async (item) => {
    await agregarWidget({ GRAIDXXX: item.GRAIDXXX })
      .unwrap()
      .then((response) => {
        aplicarEscritorio(response?.data);
        shootToast({ variant: "success", text: `${item.GRANOMXX} agregado a tu escritorio`, duration: 3000, autoHide: true });
      })
      .catch((oError) => shootToast({ variant: "error", text: oError?.data?.errors?.message ?? "No se pudo agregar el widget", duration: 4000, autoHide: true }));
  };

  const handleQuitar = async (widget) => {
    await quitarWidget({ GRAIDXXX: widget.GRAIDXXX })
      .unwrap()
      .then((response) => aplicarEscritorio(response?.data))
      .catch((oError) => shootToast({ variant: "error", text: oError?.data?.errors?.message ?? "No se pudo quitar el widget", duration: 4000, autoHide: true }));
  };

  //Reorden optimista de los ADICIONALES: se mueve local y se persiste el orden final
  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    const fijos = widgets.filter((w) => w.FIJOXXXX === "SI");
    const extras = widgets.filter((w) => w.FIJOXXXX !== "SI");
    const desde = extras.findIndex((w) => String(w.GRAIDXXX) === active.id);
    const hasta = extras.findIndex((w) => String(w.GRAIDXXX) === over.id);
    if (desde < 0 || hasta < 0) {
      return;
    }
    const nuevos = arrayMove(extras, desde, hasta);
    setWidgets([...fijos, ...nuevos]);
    await ordenarWidgets({ widgets: nuevos.map((w) => ({ GRAIDXXX: w.GRAIDXXX })) })
      .unwrap()
      .catch(() => shootToast({ variant: "error", text: "No se pudo guardar el orden", duration: 3000, autoHide: true }));
  };

  if (widgets === null) {
    return null;
  }

  const fijos = widgets.filter((w) => w.FIJOXXXX === "SI");
  const extras = widgets.filter((w) => w.FIJOXXXX !== "SI");
  const pineados = new Set(widgets.map((w) => w.GRAIDXXX));
  const disponibles = catalogo.filter((item) => !pineados.has(item.GRAIDXXX) && item.GRARUTAX);

  return (
    <Stack gap={3}>
      <Flexbox justifyContent="between" alignItems="center">
        <Stack gap={0}>
          {/* Encabezado de la página: más grande que los títulos de los tableros (17 px) — Diego 2026-09-21 */}
          <span style={{ fontSize: 26, fontWeight: 800, color: "#0D3C60", lineHeight: 1.2 }}>Mi escritorio</span>
          <Description variant="description-3">
            {`Tableros de tu cargo${cargoDes ? ` (${cargoDes})` : ""}: siempre van y de primero.`}
            {puedePersonalizar ? " Los adicionales que agregues van después y puedes moverlos o quitarlos." : ""}
          </Description>
        </Stack>
        {puedePersonalizar && (
          <Flexbox gap={2} alignItems="center">
            {esPersonal && (
              <Button type="button" variant="secondary" size="s" icon="rotate-left" onClick={() => setShowConfirmQuitar(true)} isLoading={isRestableciendo}>
                Quitar mis tableros adicionales
              </Button>
            )}
            <Button type="button" variant="secondary-bold" size="s" icon="plus" onClick={handleAbrirGaleria}>
              Agregar tablero
            </Button>
          </Flexbox>
        )}
      </Flexbox>

      {widgets.length === 0 ? (
        <Box spacing={4} rounded="md" bg="neutral-200">
          <Label color="neutral-600">
            {puedePersonalizar
              ? "Tu cargo aún no tiene plantilla de escritorio: agrega los tableros que quieras tener a la vista."
              : "Tu cargo aún no tiene plantilla de escritorio."}
          </Label>
        </Box>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1%", rowGap: 12 }}>
          {fijos.map((widget) => (
            <Tarjefij key={widget.GRAIDXXX} widget={widget} />
          ))}
          {extras.length > 0 && (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={extras.map((w) => String(w.GRAIDXXX))} strategy={rectSortingStrategy}>
                {extras.map((widget) => (
                  <Tarjewid key={widget.GRAIDXXX} widget={widget} onQuitar={handleQuitar} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {showConfirmQuitar && (
        <Delcfirm
          actionElement={{ text: `¿Quitar tus ${extras.length} tablero(s) adicional(es)? La plantilla de tu cargo se conserva.` }}
          acceptLabel="Quitar"
          handleConfirmAction={(confirm) => {
            setShowConfirmQuitar(false);
            if (confirm) {
              handleRestablecer();
            }
          }}
        />
      )}
      <Modal size="l" isOpen={showGaleria}>
        <ModalHeader>
          <Flexbox justifyContent="between" alignItems="center">
            <Label weight="bold">Agregar un tablero adicional a mi escritorio</Label>
            <Button variant="secondary" icon="close" onlyIcon size="m" onClick={() => setShowGaleria(false)} />
          </Flexbox>
        </ModalHeader>
        <ModalBody>
          <Stack gap={3}>
            {disponibles.length === 0 && (
              <Label color="neutral-600">No hay más widgets disponibles para tu perfil.</Label>
            )}
            {disponibles.map((item) => (
              <Box key={item.GRAIDXXX} spacing={3} rounded="md" border={1} borderColor="neutral-300">
                <Flexbox justifyContent="between" alignItems="center" gap={4}>
                  <Stack gap={1}>
                    <Label weight="bold">{item.GRANOMXX}</Label>
                    <Description variant="description-3">
                      {item.GRADESXX || "Sin descripción"}
                      {item.MODDESXX ? ` · ${String(item.MODDESXX).trim()}` : ""}
                    </Description>
                  </Stack>
                  <Button type="button" variant="primary-bold" size="s" icon="plus" onClick={() => handleAgregar(item)}>
                    Agregar
                  </Button>
                </Flexbox>
              </Box>
            ))}
          </Stack>
        </ModalBody>
      </Modal>
    </Stack>
  );
};

export default Miescrix;
